<template>
  <div class="home" redesign>
    <!-- Header -->
    <div class="hero">
      <div class="inner-hero">
        <div class="frow inner-hero-wrapper">
          <div class="fcolumn inner-hero-left">
            <div class="inner-hero-left-content">
              <div class="gc-logo-wrapper">
                <img :src="require(`@/assets/images/${organizationDetails.canaryBrandLogo}`)" :title="`${organizationDetails.canaryBrandTitle} powered by Canary`" />
              </div>
              <div class="sub-heading">
                <h4 v-if="organizationDetails.homepageCopy" v-html="homeCopy"></h4>
                <h4 v-else v-translate>
                  The {{organizationDetails.programName}} is a pool of funds for and funded by the
                  %{organizationDetails.name} community to support each other during financial
                  emergencies.
                </h4>
              </div>

              <!-- Company logo -->
              <LogoHeader :org="organizationDetails" />
            </div>
          </div>

          <div class="hero-illustration inner-hero-right desktop">
            <HomeSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>
    <div class="hero-illustration hidden-desktop">
      <HomeSvg :color="organizationDetails.darkBrandColor" />
    </div>

    <!-- Request Section -->
    <div class="cta-section-wrapper">
      <div class="cta-section" :class="{ 'cta-section-ineligible' : !isEligible }" v-if="organizationDetails.isDefaultRequestOrganization">
        <h1 v-translate>Request hardship assistance</h1>
        <h4 v-if="isEligible"><translate>If an unexpected emergency has made it hard for you financially, request the money you need to get back on track.</translate></h4>
        <h4
          v-else
          v-translate="{
            orgName: organizationDetails.name,
            periodLength: organizationDetails.eligibilityPeriodNumDays,
            daysLeft: user.daysUntilEligible,
            infoEmail: organizationDetails.supportEmail,
          }"
        >
          %{orgName} requires that you remain with the company for %{periodLength} days before you are eligible to request a grant. You have %{daysLeft} remaining until you become eligible. Reach out to %{infoEmail} with any questions, issues, or feedback.
        </h4>
        <button @click="onClickRequestGrantMoney" class="form-button form-button-160" :disabled="!isEligible" :class="{ 'form-button-disabled': !isEligible }">
          <span v-translate>Request</span>
        </button>
      </div>

      <!-- Repro rights Section -->
      <div v-if="organizationDetails.isReproductiveRightsOrganization" class="cta-section">
        <h1 v-translate>Request inclusive care assistance</h1>
        <h4 v-translate>If you need financial support to travel for reproductive healthcare or gender-affirming care, please request the money you need to move forward.</h4>
        <button @click="onClickRequestReproRightsMoney" class="form-button form-button-160">
          <span v-translate>Request</span>
        </button>
      </div>

      <!-- Give Section -->
      <div v-if="organizationDetails.donationsEnabled" class="cta-section">
        <h1 v-translate>Give to the {{organizationDetails.programName}}</h1>
        <h4>{{ giveSubHeaderCopy }}</h4>
        <button @click="onClickGive" class="form-button form-button-160">
          <span v-translate>Give</span>
        </button>
      </div>
    </div>

    <!-- Articles Section -->
    <!-- <LoadSpinner v-if="!requestStory" /> -->
    <div v-if="requestStory" class="articles">
      <ArticleCard
        :header="articleCard.requestStory.header"
        :subHeader="articleCard.requestStory.subHeader"
        :article="requestStory"
        :buttonText="articleCard.requestStory.buttonText"
        @click="onClickRequestGrantMoney"
      />
    </div>

    <!-- NIP 9/30/20  Avoid having two stacked loading spinners by only rendering the second one if the first is gone -->
    <!-- <LoadSpinner v-if="!contributionStory && requestStory" /> -->
    <div v-if="contributionStory && organizationDetails.donationsEnabled" class="articles-light">
      <ArticleCard
        :header="articleCard.contributionStory.header"
        :subHeader="articleCard.contributionStory.subHeader"
        :article="contributionStory"
        :buttonText="articleCard.contributionStory.buttonText"
        @click="onClickGive"
        light
      />
    </div>

    <div v-if="organizationDetails.springFourEnabled" class="articles">
      <ArticleCard
        :header="articleCard.resources.header"
        :subHeader="articleCard.resources.subHeader"
        :buttonText="articleCard.resources.buttonText"
        @click="onClickFindResources"
      />
    </div>
    <!-- End Articles Section -->

    <!-- Modals -->

    <!-- Communcation preferences modal -->
    <Modal
      v-if="communicationsModal && !demographicsModal.isOpen"
      dimmed
      :width="95"
      @close-modal="communicationsModal = false"
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <h2 class="form-field-header" v-translate>
            How would you like us to communicate with you?
          </h2>
          <div class="form-field-container">
            <p v-translate>Fill out your communication preferences on the Profile page.</p>
          </div>
        </div>
        <div class="form-footer">
          <div class="button-container">
            <router-link tag="button" class="form-button" :to="{ name: 'MyProfile' }">
              <span v-translate>Go to Profile</span>
            </router-link>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Demographic survey modal -->
    <Modal
      v-if="demographicsModal.isOpen"
      dimmed
      :width="95"
      @close-modal="demographicsModal.isOpen = false"
    >
      <div class="form-container card-shape modal-window">
        <h2 class="form-field-header" v-translate>Complete your profile</h2>
        <div class="form-field" style="margin-bottom: 0;">
          <SurveyResponseComponent
            :surveyResponse="demographicsModal.surveyResponse"
            :onlyDisplayQuestions="demographicsModal.questions"
            @survey-done="onSurveyDone"
            :forceMobile="true"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ImpactStory from '@/services/impactStories'
import Request from '@/services/requests'
import EventCategory from '@/services/eventCategories'
import ArticleCard from '@/components/ArticleCard'
import LogoHeader from '@/components/LogoHeader'
import HomeSvg from '@/views/svg/CommunitySvg'
import LoadSpinner from '@/components/LoadSpinner'
import Modal from '@/components/Modal'
import SurveyResponse from '@/services/surveyresponses'
import SurveyResponseComponent from '@/components/SurveyResponse'
import { getDefaultForCustomVariable } from '@/services/organizationCustomVariables'
import { analyticsMixin } from '@/services/googleAnalytics'
import { formatText, formatValue } from '@/services/utils'
import Auth from '@/services/auth'

export default {
  name: 'Home',
  mixins: [analyticsMixin],
  components: {
    ArticleCard,
    LogoHeader,
    LoadSpinner,
    Modal,
    SurveyResponseComponent,
    HomeSvg,
  },
  data() {
    return {
      requestStory: null,
      contributionStory: null,
      demographicsModal: {
        surveyName: 'Household Details',
        surveyResponse: null,
        questions: [96, 97], //Hardcoded demographic question HumanReadableIDs from CAN-570
        isOpen: false,
      },
      communicationsModal: false,
      eligibilityModal: true,
    }
  },
  async created() {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)

    const tokenFromSearch = urlParams.get('token')

    if (tokenFromSearch) {
      this.$store.dispatch('updateUserToken', tokenFromSearch)
      window.history.replaceState({}, null, '/')
    }

    const wasCreatedFromThirdParty = urlParams.get('was_created')
    // We get the "boolean" from Python
    if (wasCreatedFromThirdParty === 'True') {
      this.$router.push({ name: 'Registration', params: { code: 'thirdparty' } })
    }
    // Load ImpactStories
    this.loadImpactStories()


    // If user is being routed here immediately post-login:
    const { fromLogin } = this.$route.params
    if (fromLogin) {
      // If user has missing demographic questions, show demographics modal
      if (await this.demographicQuestionsIncomplete()) {
        this.demographicsModal.isOpen = true
      }
      if (this.commPrefsNotSet()) {
        this.communicationsModal = true
      }
    }
  },
  methods: {
    getDefaultForCustomVariable,
    loadImpactStories() {
      ImpactStory.api.getStories().then(stories => {
        // 0th index should always be REQUEST_STORY, 1st index should always be CONTRIBUTION_STORY
        this.requestStory = stories[0]
        this.contributionStory = stories[1]
      })
    },
    navigateToSignUp() {
      if (this.organizationDetails.enableSaml) {
        const currentLocation = window.location.href
        window.location = `${process.env.VUE_APP_API_ENDPOINT}/saml2/login?from=${currentLocation}&RelayState=${currentLocation}`
        return
      }

      this.$router.push({ name: 'Qualification' })
    },
    async navigateToLogin(loginPath = 'Login') {
      if (this.userIsLoggedIn) {
        this.$router.push({ name: loginPath })
      }
      if (this.organizationDetails.enableSaml) {
        await Auth.logout()
        const currentLocation = window.location.href
        window.location = `${process.env.VUE_APP_API_ENDPOINT}/saml2/login?from=${currentLocation}&RelayState=${currentLocation}`
        return
      }

      this.$router.push({ name: 'Login' })
    },
    onSurveyDone() {
      this.demographicsModal.isOpen = false
    },
    async onClickRequestReproRightsMoney() {
      if (this.userIsLoggedIn) {
        const newRequest = await Request.api
          .create({})
          // Update user's current request in state
          .then(request => {
            this.$store.dispatch('updateCurrentRequest', request)
            return request
          })


        const request = Request.create({
          ...newRequest,
        })

        this.$router.push({ name: 'PayPalVerify', params: {reproductive_rights: true} })
      } else {
        this.$router.push({ name: 'Qualification' })
      }
    },
    onClickRequestGrantMoney() {
      if (this.userIsLoggedIn) {
        this.$router.push({ name: 'NewRequest' })
      } else {
        this.$router.push({ name: 'Qualification' })
      }
    },
    onClickGive() {
      this.$router.push({ name: 'AddPayment' })
    },
    onClickFindResources() {
      this.eventsTracker('clicked-cta-resources')
      this.$router.push({ name: 'FindResources' })
    },
    /* Return true if employee hasn't filled out one of the two new demographics questions
     * that are now part of the Household Details onboarding survey. (CAN-570) */
    async demographicQuestionsIncomplete() {
      if (
        !this.organizationDetails.organizationSurveys.some(
          survey => survey.surveyName == this.demographicsModal.surveyName,
        )
      ) {
        return false
      }
      if (!this.user.isEmployee) {
        return false
      }
      const surveyResponse = await SurveyResponse.getOrCreate(
        this.user,
        this.demographicsModal.surveyName,
      )
      this.demographicsModal.surveyResponse = surveyResponse
      // If 1+ demographic questions are empty, return true
      return surveyResponse.questions.some(q => {
        return this.demographicsModal.questions.includes(q.humanReadableId) && q.isEmpty
      })
    },
    commPrefsNotSet() {
      return this.user.isEmployee && !this.user.hasSetNotifPreferences
    },
  },
  computed: {
    giveSubHeaderCopy() {
      const translated = this.$gettext(
        "Add money to the pool so it's there when you or your %{orgDetails} need it.",
      )
      return this.$gettextInterpolate(translated, {
        orgDetails:
          (this.organizationDetails &&
            this.organizationDetails.customVariables &&
            this.$options.filters.translateToCurrentLang(
              this.$language.current,
              this.organizationDetails.customVariables.cv2,
              this.organizationDetails.customVariables.cv2,
            )) ||
          getDefaultForCustomVariable('cv2', this.$language.current),
      })
    },
    homeCopy() {
      const varMap = { org: this.organizationDetails }
      return formatValue(this.organizationDetails.homepageCopy, varMap)
    },
    articleCard() {
      const translated = this.$gettext(
        "Add money to the pool so it's there when you or your %{orgDetails} need it.",
      )

      return {
        requestStory: {
          header: this.$gettext('Request Grant Money'),
          subHeader: this.$gettext(
            'If an unexpected emergency has made it hard for you financially, request the money you need to get back on track.',
          ),
          buttonText: this.$gettext('Request'),
        },
        contributionStory: {
          header: this.$gettext('Give to the') + this.organizationDetails.programName,
          subHeader: this.$gettextInterpolate(translated, {
            orgDetails:
              (this.organizationDetails &&
                this.organizationDetails.customVariables &&
                this.$options.filters.translateToCurrentLang(
                  this.$language.current,
                  this.organizationDetails.customVariables.cv2,
                  this.organizationDetails.customVariables.cv2,
                )) ||
              getDefaultForCustomVariable('cv2', this.$language.current),
          }),
          buttonText: this.$gettext('Give'),
        },
        resources: {
          header: this.$gettext('Find money-saving resources'),
          subHeader: this.$gettext(
            'Connect with local, trusted resources designed to help you lower monthly costs and achieve financial stability.',
          ),
          buttonText: this.$gettext('Find Resources'),
        },
      }
    },
    isEligible() {
      if(this.user) {
        return this.user.isEligible
      }
      return true
    },
    ...mapState(['organizationDetails', 'user']),
    ...mapGetters(['userIsLoggedIn']),
  },
  watch: {
    '$language.current': function updateLanguage() {
      this.loadImpactStories()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/flex-box';
@import '@/styles/mixins/responsive';

.hero {
  background-color: var(--light-brand-color);
  border-top-left-radius: 80px;
  border-bottom-right-radius: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 1rem;

  @include tablet-screen {
    border-radius: 80px 0 80px 0;
    padding: 2rem;
  }

  @include desktop-screen {
    border-radius: 120px 0 120px 0;
    padding: 4rem 2rem;
  }

  .inner-hero {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: #ffffff;
    border-radius: 64px 0 64px 0;
    padding: 60px 24px;
    z-index: 9;
    width: 100%;

    @include tablet-screen {
      border-radius: 48px 0;
      padding: 4rem;
    }

    @include desktop-screen {
      background-color: transparent;
      padding: 0;
    }

    .inner-hero-wrapper {
      justify-content: space-around;
      width: 100%;

      @include tablet-screen {
        justify-content: center;
      }
    }

    .inner-hero-left,
    .inner-hero-right {
      align-content: space-evenly;
      align-items: center;
      justify-content: center;

      @include desktop-screen {
        width: 50%;
      }
    }

    .inner-hero-left {
      @include mobile-screen {
        max-width: 500px;
      }

      @include tablet-screen {
        max-width: 500px;
      }

      @include desktop-screen {
        min-width: 350px;
        max-width: 500px;
        margin: 0 2rem;
        justify-content: center;
      }
    }

    .inner-hero-left-content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .inner-hero-right {
      overflow: hidden;
      border-radius: 80px 0;
    }
  }
}

.hero-illustration {
  z-index: 8;

  &.hidden-desktop {
    border-radius: 0 0 64px 0;
    position: relative;
    top: -120px;
    height: 310px;
    overflow: hidden;
    margin-bottom: -120px;

    @include tablet-screen {
      height: 475px;
      top: -280px;
      margin-bottom: -280px;
    }
  }

  &.desktop {
    svg {
      min-height: 100%;
    }
  }
}

.gc-logo-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  img {
    width: 70%;
  }
}

.sub-heading {
  font-size: 1.25rem;
  margin-bottom: 2rem;
  text-align: center;

  h4 {
    margin-bottom: 0;
  }

  ::v-deep p {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 0;
  }

  ::v-deep p:last-of-type {
    margin-bottom: 0;
  }
}

.articles {
  background-color: var(--light-brand-color);
  border-radius: 5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 2.5rem;
  margin-top: 2.5rem;

  &-light {
    background-color: white;
  }

  @include tablet-and-up-screen {
    border-radius: 7.5rem 0;
  }
}

.cta-section-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin: 0rem 1rem;

  @include tablet-screen {
    flex-direction: row;
    margin: 2.5rem 2rem;

    .cta-section {
      width: 50%;
      align-self: stretch;
      margin-bottom: 2.5rem;

      &:nth-of-type(1) {
        margin-right: 1rem;
      }

      &:nth-of-type(2) {
        margin-left: 1rem;
      }
    }
  }

  @include desktop-screen {
    flex-direction: row;
    padding: 4rem 0;

    .cta-section {
      max-width: 420px;
      margin-top: 0;
      padding: 0;
      align-self: stretch;

      &.cta-section-ineligible {
        max-width: 600px;
      }

      h1 {
        font-size: 2.25rem;
      }
    }
  }
}

.join-now,
.cta-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 2.5rem;
  align-self: center;
  max-width: 90%;
  justify-content: space-between;

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 2rem;
  }

  h4 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 2rem;
  }

  h5 {
    margin-bottom: 0;

    span {
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }

  .cta-section-ineligible {

  }
}

.logo-wrapper {
  height: 12rem;
  width: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
